import { useState, useCallback } from 'react'
import { auth } from '../../../firebase'
import { BRAND_VENDOR } from '../../../config/deploy'
import { isFragrance, normalizeFragranceDraft, buildFragrancePrompt } from '../utils/fragranceDraft.mjs'

/**
 * AI Processing for product listings
 * Uses Claude API to analyze images and generate SEO content
 */

// Shopify metafields that Google requires
const GOOGLE_METAFIELDS = [
  'product_type',
  'brand',
  'material',
  'gender',
  'age_group',
  'color',
  'size',
  'weight',
  'weight_unit',
  'condition',
  'gtin', // UPC/EAN
  'mpn', // Manufacturer Part Number
]

const TAG_PRINT_INFO_MAX = 24
const CLAUDE_MAX_IMAGE_BYTES = 5 * 1024 * 1024
const CLAUDE_SUPPORTED_IMAGE_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif'
])

const LISTING_FIELD_LABELS = {
  title: 'Title',
  description: 'Description',
  metaTitle: 'Meta Title',
  metaDescription: 'Meta Description',
  urlSlug: 'URL Slug',
  tags: 'Tags',
  category: 'Category',
  subcategory: 'Audience',
  productType: 'Product Type',
  weight: 'Weight',
  'metafields.material': 'Material',
  'metafields.brand': 'Brand',
  'metafields.gender': 'Gender',
  'metafields.age_group': 'Age Group',
  'metafields.color': 'Color',
  'metafields.size': 'Size',
  'metafields.weight_unit': 'Weight Unit',
  'metafields.condition': 'Condition',
  'metafields.product_type': 'Product Type',
  'metafields.gtin': 'GTIN',
  'metafields.mpn': 'MPN',
  'metafields.stone_type': 'Stone Type',
  'metafields.stone_carat': 'Stone Carat',
  'metafields.karat': 'Karat',
  'metafields.ring_size': 'Ring Size'
}

const CATEGORY_DISPLAY_LABELS = {
  rings: 'Ring',
  chains: 'Chain',
  bracelets: 'Bracelet',
  necklaces: 'Necklace',
  earrings: 'Earrings',
  pendants: 'Pendant',
  watches: 'Watch',
  other: 'Jewelry'
}

/**
 * Generate product data using AI
 * @param {string} imageBase64 - Base64 encoded image
 * @param {string} description - User's description in PT or EN
 * @param {Object} priceInfo - { costPrice, salePrice }
 */
export function useAIProcessing() {
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState(null)
  const [isConfigured, setIsConfigured] = useState(true)

  const processProduct = useCallback(async (imageBase64, description, priceInfo) => {
    setProcessing(true)
    setError(null)

    try {
      const user = auth.currentUser
      const idToken = await user?.getIdToken()
      if (!idToken) {
        setError('You must be logged in to use Claude product generation.')
        return null
      }

      const normalizedImage = await normalizeClaudeImageInput(imageBase64)
      const requestBody = {
        prompt: buildPrompt(description, priceInfo)
      }
      if (normalizedImage) {
        requestBody.imageBase64 = normalizedImage
      }

      let response = await fetch('/api/ai/product', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify(requestBody)
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        const message = getApiErrorMessage(errorData, response.status)
        if (response.status === 412 || errorData.error === 'ai-not-configured') {
          setIsConfigured(false)
          setError('Claude API is not configured. Configure it in Control Hub → Integrations, then try again with the same photo and information.')
          return null
        }

        // Retry once without image when upstream rejects the image payload.
        if (response.status === 400 && requestBody.imageBase64) {
          response = await fetch('/api/ai/product', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${idToken}`
            },
            body: JSON.stringify({ prompt: requestBody.prompt })
          })
          if (response.ok) {
            const retryData = await response.json()
            return parseAndAttachSku(retryData, {
              sellerDescription: description,
              priceInfo
            })
          }
          const retryErrorData = await response.json().catch(() => ({}))
          throw new Error(getApiErrorMessage(retryErrorData, response.status) || message)
        }

        throw new Error(message)
      }

      const data = await response.json()
      return parseAndAttachSku(data, {
        sellerDescription: description,
        priceInfo
      })
    } catch (err) {
      console.error('AI processing failed:', err)
      setError(buildAiRetryMessage(err?.message))
      return null
    } finally {
      setProcessing(false)
    }
  }, [])

  const regenerateFields = useCallback(async (imageUrl, currentData) => {
    if (!imageUrl) return null
    setProcessing(true)
    setError(null)

    try {
      const user = auth.currentUser
      const idToken = await user?.getIdToken()
      if (!idToken) {
        setError('You must be logged in to use AI.')
        return null
      }

      const normalizedImage = await normalizeClaudeImageInput(imageUrl)
      const perfumeContext = {
        sellerDescription: `${currentData.title || ''}\n${currentData.description || ''}`,
        priceInfo: { fragrance: currentData.metafields || {}, weight: currentData.weight }
      }
      const requestBody = {
        prompt: isFragrance(currentData)
          ? buildFragrancePrompt(perfumeContext.sellerDescription, perfumeContext.priceInfo)
          : `Regenerate product listing fields for this existing jewelry or fragrance product. For perfume, keep true manufacturer brand, volume_ml and concentration; never introduce metals, gemstones or scent claims not present in the current data.
Current data: Title: "${currentData.title || ''}", Type: "${currentData.productType || ''}", Tags: "${currentData.tags || ''}"
Price: $${currentData.price || '0'}

Return ONLY valid JSON with: title, description (HTML), productType, tags (array), metafields (existing product fields including fragrance_name, concentration, volume_ml, fragrance_family, top_notes, heart_notes, base_notes and packaging for perfume).
Keep the same creative style as the original but improve if possible.`
      }
      if (normalizedImage) {
        requestBody.imageBase64 = normalizedImage
      }

      let response = await fetch('/api/ai/product', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify(requestBody)
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        if (response.status === 400 && requestBody.imageBase64) {
          response = await fetch('/api/ai/product', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${idToken}`
            },
            body: JSON.stringify({ prompt: requestBody.prompt })
          })
        }
        if (!response.ok) {
          const retryErrorData = await response.json().catch(() => ({}))
          throw new Error(getApiErrorMessage(retryErrorData, response.status) || getApiErrorMessage(errorData, response.status))
        }
      }

      const data = await response.json()
      const content = data.content?.[0]?.text || ''
      const jsonMatch = content.match(/\{[\s\S]*\}/)
      if (jsonMatch) {
        const parsed = JSON.parse(jsonMatch[0])
        return isFragrance(currentData) ? normalizeFragranceDraft(parsed, perfumeContext) : parsed
      }
      throw new Error('Failed to parse AI response')
    } catch (err) {
      console.error('AI regeneration failed:', err)
      setError(err.message)
      return null
    } finally {
      setProcessing(false)
    }
  }, [])

  const generateUpdateSuggestions = useCallback(async (payload = {}) => {
    setProcessing(true)
    setError(null)

    try {
      const user = auth.currentUser
      const idToken = await user?.getIdToken()
      if (!idToken) {
        throw new Error('You must be logged in to use AI.')
      }

      const normalizedImage = await normalizeClaudeImageInput(payload.imageBase64)
      const requestBody = {
        prompt: buildUpdateSuggestionsPrompt(payload)
      }
      if (normalizedImage) {
        requestBody.imageBase64 = normalizedImage
      }

      const response = await fetch('/api/ai/product', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify(requestBody)
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        if (response.status === 412 || errorData.error === 'ai-not-configured') {
          setIsConfigured(false)
          throw new Error('Claude API not configured. Go to Control Hub → Integrations to add your API key.')
        }
        throw new Error(getApiErrorMessage(errorData, response.status))
      }

      const data = await response.json()
      const parsed = parseAiJsonPayload(data)
      if (!parsed) {
        throw new Error('Failed to parse AI response')
      }

      if (isFragrance(payload)) {
        const normalized = normalizeFragranceDraft(parsed, {
          sellerDescription: `${payload.title || ''}\n${payload.description || ''}`,
          priceInfo: { fragrance: payload.metafields || {}, weight: payload.weight }
        })
        return {
          ...normalized,
          summary: 'Review perfume details against the label or supplier.',
          metafields: { ...normalized.metafields, tag_print_info: normalized.tagPrintInfo }
        }
      }

      const parsedMetafields = parsed.metafields && typeof parsed.metafields === 'object'
        ? parsed.metafields
        : {}
      const normalizedTags = Array.isArray(parsed.tags)
        ? parsed.tags.map((tag) => String(tag || '').trim()).filter(Boolean)
        : String(parsed.tags || '').split(',').map((tag) => tag.trim()).filter(Boolean)
      const tagPrintCandidate = parsed.tagPrintInfo
        || parsed.tag_print_info
        || parsedMetafields.tag_print_info
        || ''
      const weightCandidate = parsed.weight ?? parsedMetafields.weight ?? payload.weight ?? ''

      return {
        title: String(parsed.title || '').trim(),
        description: String(parsed.description || '').trim(),
        tags: normalizedTags,
        weight: String(weightCandidate || '').trim(),
        tagPrintInfo: String(tagPrintCandidate || '').trim().slice(0, TAG_PRINT_INFO_MAX),
        summary: String(parsed.summary || '').trim(),
        metafields: {
          karat: String(parsedMetafields.karat || '').trim(),
          stone_type: String(parsedMetafields.stone_type || '').trim(),
          stone_carat: String(parsedMetafields.stone_carat || '').trim(),
          weight: String(parsedMetafields.weight || weightCandidate || '').trim(),
          tag_print_info: String(tagPrintCandidate || '').trim().slice(0, TAG_PRINT_INFO_MAX)
        }
      }
    } catch (err) {
      console.error('AI update suggestions failed:', err)
      setError(err.message || 'AI update suggestions failed')
      throw err
    } finally {
      setProcessing(false)
    }
  }, [])

  const generateGoogleMetadataFix = useCallback(async (payload = {}) => {
    setProcessing(true)
    setError(null)

    try {
      const normalizedMissingKeys = normalizeGoogleMissingFieldKeys(payload?.missingFields)
      if (!normalizedMissingKeys.length) {
        return {
          metafields: {},
          productType: '',
          summary: 'No missing Google fields were provided.',
          reviewFlags: [],
          missingFields: []
        }
      }

      const deterministic = buildDeterministicGoogleFixes(payload)
      const resolvedMetafields = {}
      normalizedMissingKeys.forEach((key) => {
        const value = deterministic.metafields[key]
        if (value) resolvedMetafields[key] = value
      })

      const unresolvedKeys = normalizedMissingKeys.filter((key) => !resolvedMetafields[key])
      if (!unresolvedKeys.length) {
        return {
          metafields: resolvedMetafields,
          productType: deterministic.productType || '',
          summary: 'Filled missing Google fields from the current Shopify data.',
          reviewFlags: deterministic.reviewFlags || [],
          missingFields: []
        }
      }

      const user = auth.currentUser
      const idToken = await user?.getIdToken()
      if (!idToken) {
        throw new Error('You must be logged in to use AI.')
      }

      const normalizedImage = await normalizeClaudeImageInput(payload.imageBase64)
      const requestBody = {
        prompt: buildGoogleMetadataFixPrompt(payload, {
          deterministic,
          unresolvedKeys
        })
      }
      if (normalizedImage) {
        requestBody.imageBase64 = normalizedImage
      }

      let response = await fetch('/api/ai/product', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify(requestBody)
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        if (response.status === 412 || errorData.error === 'ai-not-configured') {
          setIsConfigured(false)
          throw new Error('Claude API is not configured. Configure it in Control Hub → Integrations, then try again.')
        }

        if (response.status === 400 && requestBody.imageBase64) {
          response = await fetch('/api/ai/product', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${idToken}`
            },
            body: JSON.stringify({ prompt: requestBody.prompt })
          })
        }

        if (!response.ok) {
          const retryErrorData = await response.json().catch(() => ({}))
          throw new Error(getApiErrorMessage(retryErrorData, response.status) || getApiErrorMessage(errorData, response.status))
        }
      }

      const data = await response.json()
      const parsed = parseAiJsonPayload(data)
      if (!parsed) {
        throw new Error('Failed to parse AI response')
      }

      const normalized = normalizeGoogleMetadataFixResponse(parsed, {
        payload,
        missingKeys: unresolvedKeys
      })
      const metafields = {
        ...resolvedMetafields,
        ...(normalized.metafields || {})
      }
      const missingFields = normalizedMissingKeys
        .filter((key) => !String(metafields[key] || '').trim())
        .map((key) => `metafields.${key}`)

      return {
        metafields,
        productType: normalized.productType || deterministic.productType || '',
        summary: normalized.summary || 'Filled Google marketplace fields from current product data and Claude review.',
        reviewFlags: mergeReviewFlags([
          ...(deterministic.reviewFlags || []),
          ...(normalized.reviewFlags || [])
        ]),
        missingFields
      }
    } catch (err) {
      console.error('AI Google metadata fix failed:', err)
      setError(buildAiRetryMessage(err?.message))
      throw err
    } finally {
      setProcessing(false)
    }
  }, [])

  return {
    processProduct,
    regenerateFields,
    generateUpdateSuggestions,
    generateGoogleMetadataFix,
    processing,
    error,
    isConfigured
  }
}

function getApiErrorMessage(errorData, status) {
  const raw = errorData?.error
  if (typeof raw === 'string' && raw.trim()) return raw.trim()
  if (typeof raw?.message === 'string' && raw.message.trim()) return raw.message.trim()
  if (typeof errorData?.message === 'string' && errorData.message.trim()) return errorData.message.trim()
  return `API error: ${status}`
}

const SKU_TYPE_CODES = {
  perfume: '15', perfumes: '15', fragrance: '15', perfumaria: '15',
  ring: '01', rings: '01', anel: '01',
  pendant: '02', pendants: '02', pingente: '02',
  necklace: '03', necklaces: '03', colar: '03',
  chain: '04', chains: '04', corrente: '04',
  bracelet: '05', bracelets: '05', pulseira: '05', bangle: '05',
  earring: '06', earrings: '06', brinco: '06', brincos: '06',
  watch: '07', watches: '07', relogio: '07', relógio: '07',
  brooch: '08', brooches: '08', broche: '08',
  anklet: '09', anklets: '09', tornozeleira: '09',
  cufflink: '10', cufflinks: '10', abotoadura: '10', abotoaduras: '10',
  charm: '11', charms: '11', berloque: '11', berloques: '11',
  masonic: '12',
  coin: '13', coins: '13', medal: '13', medalha: '13', moeda: '13',
  set: '14', conjunto: '14',
  other: '99', jewelry: '99', accessory: '99', acessorio: '99'
}

const CATEGORY_SHORT_LABELS = {
  ring: 'RING', rings: 'RING', anel: 'RING',
  pendant: 'PEND', pendants: 'PEND', pingente: 'PEND',
  necklace: 'NECK', necklaces: 'NECK', colar: 'NECK',
  chain: 'CHAIN', chains: 'CHAIN', corrente: 'CHAIN',
  bracelet: 'BRACE', bracelets: 'BRACE', pulseira: 'BRACE',
  earring: 'EARR', earrings: 'EARR', brinco: 'EARR',
  watch: 'WATCH', watches: 'WATCH',
  other: 'JEWL'
}

function toNormalizedKey(value) {
  return String(value || '').toLowerCase().trim()
}

function normalizeWeightToken(value) {
  const text = String(value || '').trim().replace(',', '.')
  const n = Number.parseFloat(text)
  if (!Number.isFinite(n) || n <= 0) return ''
  return Number.isInteger(n) ? `${n}` : `${n.toFixed(2).replace(/\.?0+$/, '')}`
}

function extractKarat(value) {
  const match = String(value || '').match(/(\d{1,2})/)
  return match ? match[1] : ''
}

function buildTechnicalMetalToken(payload = {}) {
  const metafields = payload?.metafields || {}
  const karatDigits = extractKarat(metafields?.karat || payload?.detectedInfo?.karat || '')
  const materialText = [
    metafields?.material,
    metafields?.color,
    payload?.detectedInfo?.metalType,
    payload?.title
  ]
    .map((item) => String(item || '').toLowerCase())
    .join(' ')

  if (/(silver|prata)/.test(materialText)) return 'SIL'
  if (/(platinum|platina)/.test(materialText)) return 'PLAT'
  if (/(gold|ouro)/.test(materialText) || karatDigits) {
    return karatDigits ? `AU${karatDigits}K` : 'AU'
  }
  return karatDigits ? `AU${karatDigits}K` : 'AU'
}

function buildTechnicalColorToken(payload = {}) {
  const metafields = payload?.metafields || {}
  const colorText = [
    metafields?.material,
    metafields?.color,
    payload?.detectedInfo?.metalType
  ].join(' ')
  if (!/(gold|ouro|10k|14k|18k|22k|24k|white gold|yellow gold|rose gold)/i.test(colorText)) {
    return ''
  }
  const normalizedColor = normalizeColorValue(colorText)

  if (normalizedColor === 'White') return 'WG'
  if (normalizedColor === 'Yellow') return 'YG'
  if (normalizedColor === 'Rose') return 'RG'
  return ''
}

function buildTechnicalTagPrintInfo(payload = {}) {
  const metafields = payload?.metafields || {}
  const metalToken = buildTechnicalMetalToken(payload)
  const colorToken = metalToken.startsWith('AU') ? buildTechnicalColorToken(payload) : ''

  const productTypeText = String(payload?.productType || '').trim()
  const shortFromType = productTypeText
    .split(/\s+/)
    .map((part) => part.replace(/[^A-Za-z0-9]/g, ''))
    .find(Boolean)
  const categoryKey = toNormalizedKey(payload?.category)
  const itemToken = (shortFromType || CATEGORY_SHORT_LABELS[categoryKey] || 'JEWL')
    .toUpperCase()
    .slice(0, 6)

  const stoneCarat = normalizeWeightToken(metafields?.stone_carat)
  const stoneToken = stoneCarat ? `${stoneCarat}CT` : ''

  const weightValue = normalizeWeightToken(payload?.weight || metafields?.weight || '')
  const weightToken = weightValue ? `${weightValue}G` : ''

  const base = [metalToken, colorToken, itemToken, stoneToken]
    .filter(Boolean)
    .join(' ')
  if (weightToken && `${base} ${weightToken}`.trim().length <= TAG_PRINT_INFO_MAX) {
    return `${base} ${weightToken}`.trim()
  }
  return base.slice(0, TAG_PRINT_INFO_MAX)
}

async function parseAndAttachSku(data, context = {}) {
  const parsedPayload = parseAiJsonPayload(data)
  if (!parsedPayload) {
    throw new Error('Failed to parse AI response')
  }

  const parsed = normalizeListingPayload(parsedPayload, context)
  const category = parsed.skuCategory || parsed.category || 'other'
  try {
    const idToken = await auth.currentUser?.getIdToken()
    parsed.sku = await fetchSequentialSKU(category, idToken)
  } catch {
    parsed.sku = generateFallbackSKU(category)
  }

  if (!parsed.metafields || typeof parsed.metafields !== 'object') {
    parsed.metafields = {}
  }
  const technicalTag = isFragrance(parsed) ? parsed.tagPrintInfo : buildTechnicalTagPrintInfo(parsed)
  if (technicalTag) {
    parsed.tagPrintInfo = technicalTag
    parsed.metafields.tag_print_info = technicalTag
  }

  if (!parsed.weight && parsed.metafields?.weight) {
    parsed.weight = String(parsed.metafields.weight).trim()
  }

  return parsed
}

/**
 * Build the prompt for Claude
 */
/**
 * SKU Format: 17-XX-XXXX (all numbers for easy POS search)
 * - 17 = brand prefix (A=1, G=7 in the reference deployment)
 * - XX = Type code: 01=Ring, 02=Pendant, 03=Necklace, 04=Chain, 05=Bracelet, 06=Earrings...
 * - XXXX = Sequential 4-digit code (from backend API)
 */

// Fetch sequential SKU from backend API
async function fetchSequentialSKU(category, idToken) {
  try {
    const response = await fetch(`/api/sku/next?category=${encodeURIComponent(category)}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json'
      }
    })

    if (!response.ok) {
      console.warn('Failed to fetch sequential SKU, falling back to timestamp')
      return generateFallbackSKU(category)
    }

    const data = await response.json()
    return data.sku
  } catch (err) {
    console.warn('SKU API error, using fallback:', err)
    return generateFallbackSKU(category)
  }
}

function generateFallbackSKU(category) {
  const categoryCode = SKU_TYPE_CODES[toNormalizedKey(category)] || '99'
  // Use last 4 digits of timestamp as fallback
  const timestamp = String(Date.now()).slice(-4)
  return `17${categoryCode}${timestamp}`
}

export function buildPrompt(description, priceInfo = {}) {
  if (priceInfo.productFamily === 'perfumes' || (priceInfo.productFamily !== 'jewelry' && isFragrance({title:description}))) return buildFragrancePrompt(description, priceInfo)
  const compareAtInfo = priceInfo.compareAtPrice
    ? `- Compare-at price: $${priceInfo.compareAtPrice}`
    : ''
  const weightInfo = priceInfo.weight
    ? `- Weight provided by seller/user: ${priceInfo.weight}g. Use this exact value.`
    : '- Weight: only use a weight if it is explicitly written in the seller description or clearly visible on a scale in the image. Otherwise return an empty string.'

  const fragranceInstructions = priceInfo.productFamily !== 'jewelry' ? `FIRST classify the product in the photo. If it is perfume/fragrance, follow this fragrance branch and ignore all jewelry-specific instructions below:\n${buildFragrancePrompt(description, priceInfo)}\nOtherwise follow the jewelry branch below.\n` : ''
  return `${fragranceInstructions}You are ${BRAND_VENDOR}'s internal Shopify jewelry listing assistant for office staff.

Your job is to convert one jewelry photo plus one seller description into a Shopify-ready draft that is factual, searchable, and commercially usable.

SELLER DESCRIPTION (may be Portuguese or English):
"${description}"

PRICE INFO:
- Cost: $${priceInfo.costPrice}
- Sale Price: $${priceInfo.salePrice}
${compareAtInfo}
${weightInfo}

WORK IN 2 PASSES:
1) Extract only confirmed facts from the seller description and image.
2) Compose a Shopify-ready draft in English using those confirmed facts.

OUTPUT LANGUAGE:
- Return title, description, metaTitle, metaDescription, tags, and review reasons in English.
- Translate confirmed facts from Portuguese into English when needed.
- Internal shorthand may stay concise, but storefront text must read naturally in English.

STRICT FACT RULES:
- Use only facts explicitly stated in the seller description or clearly visible in the image.
- Do not invent luxury stories, symbolism, certifications, origin stories, warranty claims, or unseen specs.
- If a value is uncertain, return an empty string instead of guessing.
- If the category is unclear, choose "other" and add a review flag.
- If the seller uses a strong product phrase, preserve it. Example: if the seller says "half round diamond ring", keep "Half Round Diamond Ring" in the title instead of replacing it with a generic count-based title.
- If the seller gives metal details such as "AU 18k Branco" or "18k white gold", map them into material/color/karat correctly.
- productType is an internal label, factual, max 20 characters.
- category must be one of: rings, chains, bracelets, necklaces, earrings, pendants, watches, other.
- skuCategory must match the category chosen for SKU generation.

SEO + STOREFRONT RULES:
- Title must be strong, specific, and catalog-ready. Avoid weak titles like "Diamond Ring with 9 Stones" when a better seller phrase exists.
- Description must be clean HTML with this exact structure:
  1) first SEO paragraph
  2) second short paragraph
  3) one <ul> of confirmed specs
  4) one <h3>Q&A</h3> followed by one <ul> with 2-3 short factual Q&A items
- Description must sound storefront-ready, not like raw notes.
- Meta title must be specific and under 60 characters.
- Meta description must be specific, SEO-friendly, and under 160 characters.
- Tags must be useful Shopify tags based on confirmed facts. Prefer 6-12 tags, including phrase tags when helpful. Avoid generic filler like only "jewelry" or "ring".

STONE + WEIGHT RULES:
- If stone count and millimeter sizes are provided, you may estimate total stone carat in metafields.stone_carat.
- Any estimated stone carat must also create a reviewFlags entry explaining that the total carat is estimated from count plus dimensions and must be verified.
- Millimeter stone measurements may be used for stone carat estimation only.
- Never convert millimeter measurements into product gram weight.
- weight must be numeric grams only with no unit text, and only when explicitly written in text or clearly visible on a scale in the image.

REVIEW RULES:
- reviewFlags must list any field that still needs manual confirmation.
- missingFields must list unresolved field ids using dot notation such as "metafields.karat".

FILL WHAT YOU CAN FOR SHOPIFY:
- title
- description
- metaTitle
- metaDescription
- urlSlug
- tags
- category
- subcategory
- productType
- skuCategory
- weight
- metafields.product_type
- metafields.brand
- metafields.material
- metafields.gender
- metafields.age_group
- metafields.color
- metafields.size
- metafields.weight
- metafields.weight_unit
- metafields.condition
- metafields.gtin
- metafields.mpn
- metafields.stone_type
- metafields.stone_carat
- metafields.karat
- metafields.ring_size

Return ONLY valid JSON with this exact structure:

{
  "title": "Specific catalog title in English",

  "description": "<p>SEO paragraph.</p><p>Second short paragraph.</p><ul><li>Confirmed spec</li></ul><h3>Q&A</h3><ul><li><strong>Question?</strong> Answer.</li></ul>",

  "metaTitle": "Short SEO title under 60 chars",

  "metaDescription": "SEO description under 160 chars",

  "urlSlug": "simple-factual-url-slug",

  "tags": ["specific", "shopify", "phrase tags"],

  "category": "rings|chains|bracelets|necklaces|earrings|pendants|watches|other",

  "subcategory": "mens|womens|unisex|",

  "productType": "Short factual label text, max 20 chars",

  "skuCategory": "Return ONLY the category name for SKU: earrings|rings|bracelets|necklaces|chains|pendants|watches|other",

  "weight": "Weight in grams as number only, or empty string",

  "metafields": {
    "product_type": "Jewelry",
    "brand": "${BRAND_VENDOR}",
    "material": "",
    "gender": "",
    "age_group": "adult",
    "color": "",
    "size": "",
    "weight": "",
    "weight_unit": "g",
    "condition": "new",
    "gtin": "",
    "mpn": "",
    "stone_type": "",
    "stone_carat": "",
    "karat": "",
    "ring_size": ""
  },

  "detectedInfo": {
    "metalType": "",
    "karat": "",
    "estimatedWeight": "",
    "stones": [],
    "style": "",
    "occasion": []
  },

  "reviewFlags": [
    {
      "field": "metafields.stone_carat",
      "reason": "Total stone carat is estimated from seller-provided stone count and millimeter measurements. Verify before publish.",
      "severity": "recommended"
    }
  ],

  "missingFields": ["metafields.karat", "metafields.stone_carat"]
}

Do not add any text before or after the JSON.`
}

async function toClaudeImageInput(imageBase64) {
  const raw = String(imageBase64 || '').trim()
  if (raw.startsWith('data:image/') && raw.includes(';base64,')) {
    return raw
  }
  if (!/^https?:\/\//i.test(raw)) return ''

  try {
    const response = await fetch(raw)
    if (!response.ok) return ''
    const contentType = String(response.headers.get('content-type') || 'image/jpeg').toLowerCase()
    if (!contentType.startsWith('image/')) return ''

    const buffer = await response.arrayBuffer()
    if (!buffer.byteLength) return ''
    const base64 = arrayBufferToBase64(buffer)
    if (!base64) return ''
    return `data:${contentType};base64,${base64}`
  } catch (err) {
    console.warn('Could not load product image for AI suggestions:', err)
    return ''
  }
}

function parseDataUrlImage(dataUrl) {
  const match = String(dataUrl || '').trim().match(/^data:(image\/[a-zA-Z0-9.+-]+);base64,([A-Za-z0-9+/=\r\n]+)$/)
  if (!match) return null
  return {
    mediaType: String(match[1] || '').toLowerCase(),
    data: String(match[2] || '').replace(/\s+/g, '')
  }
}

function estimateBase64SizeBytes(base64Data) {
  const data = String(base64Data || '')
  if (!data) return 0
  const padding = data.endsWith('==') ? 2 : data.endsWith('=') ? 1 : 0
  return Math.max(0, Math.floor((data.length * 3) / 4) - padding)
}

function imageFromDataUrl(dataUrl) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error('Image decode failed'))
    img.src = dataUrl
  })
}

async function renderJpegDataUrl(dataUrl, maxDimension = 2000, quality = 0.9) {
  const img = await imageFromDataUrl(dataUrl)
  const srcWidth = Number(img.naturalWidth || img.width || 0)
  const srcHeight = Number(img.naturalHeight || img.height || 0)
  if (!srcWidth || !srcHeight) return ''

  const scale = Math.min(1, maxDimension / Math.max(srcWidth, srcHeight))
  const width = Math.max(1, Math.round(srcWidth * scale))
  const height = Math.max(1, Math.round(srcHeight * scale))

  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')
  if (!ctx) return ''
  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, width, height)
  ctx.drawImage(img, 0, 0, width, height)
  return canvas.toDataURL('image/jpeg', quality)
}

async function shrinkDataUrlForClaude(dataUrl) {
  let candidate = String(dataUrl || '').trim()
  let maxDimension = 2200
  let quality = 0.9

  for (let attempt = 0; attempt < 7; attempt += 1) {
    try {
      const next = await renderJpegDataUrl(candidate, maxDimension, quality)
      if (!next) break
      const parsed = parseDataUrlImage(next)
      if (!parsed) break
      candidate = next
      const size = estimateBase64SizeBytes(parsed.data)
      if (size <= CLAUDE_MAX_IMAGE_BYTES && CLAUDE_SUPPORTED_IMAGE_TYPES.has(parsed.mediaType)) {
        return candidate
      }
    } catch {
      break
    }
    maxDimension = Math.max(900, Math.round(maxDimension * 0.82))
    quality = Math.max(0.42, quality - 0.1)
  }

  const parsed = parseDataUrlImage(candidate)
  if (!parsed) return ''
  if (!CLAUDE_SUPPORTED_IMAGE_TYPES.has(parsed.mediaType)) return ''
  if (estimateBase64SizeBytes(parsed.data) > CLAUDE_MAX_IMAGE_BYTES) return ''
  return candidate
}

async function normalizeClaudeImageInput(imageInput) {
  const asDataUrl = await toClaudeImageInput(imageInput)
  const parsed = parseDataUrlImage(asDataUrl)
  if (!parsed) return ''

  const supported = CLAUDE_SUPPORTED_IMAGE_TYPES.has(parsed.mediaType)
  const withinSize = estimateBase64SizeBytes(parsed.data) <= CLAUDE_MAX_IMAGE_BYTES
  if (supported && withinSize) {
    return asDataUrl
  }

  const shrunk = await shrinkDataUrlForClaude(asDataUrl)
  return shrunk || ''
}

function arrayBufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  const chunkSize = 0x8000
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize)
    binary += String.fromCharCode(...chunk)
  }
  return btoa(binary)
}

function stripHtml(value) {
  return String(value || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

function safePromptValue(value, maxLen = 1200) {
  return stripHtml(value)
    .replace(/"/g, "'")
    .slice(0, maxLen)
}

function parseAiJsonPayload(data) {
  const content = data?.content?.[0]?.text || ''
  const jsonMatch = content.match(/\{[\s\S]*\}/)
  if (!jsonMatch) return null
  return JSON.parse(jsonMatch[0])
}

function buildAiRetryMessage(message) {
  const clean = String(message || '').trim()
  if (!clean) {
    return 'Claude could not generate product details. Try again with the same photo and information.'
  }
  if (/try again/i.test(clean)) return clean
  if (/configured|logged in|permission|unauth/i.test(clean)) return clean
  return `${clean} Try again with the same photo and information.`
}

export function normalizeListingPayload(raw, context = {}) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    throw new Error('Claude did not return product data in the expected format.')
  }

  if (context.priceInfo?.productFamily === 'perfumes' || (context.priceInfo?.productFamily !== 'jewelry' && (isFragrance(raw) || isFragrance({title:context.sellerDescription})))) return normalizeFragranceDraft(raw, context)

  const sellerFacts = extractSellerFacts(context.sellerDescription)
  const metafields = raw.metafields && typeof raw.metafields === 'object' && !Array.isArray(raw.metafields)
    ? { ...raw.metafields }
    : {}
  const detectedInfo = raw.detectedInfo && typeof raw.detectedInfo === 'object' && !Array.isArray(raw.detectedInfo)
    ? { ...raw.detectedInfo }
    : {}

  const category = normalizeListingCategory(
    raw.category || raw.skuCategory || sellerFacts.category || raw.productType || raw.title || sellerFacts.titleSeed
  )
  const skuCategory = normalizeListingCategory(
    raw.skuCategory || raw.category || sellerFacts.category || raw.productType || raw.title || sellerFacts.titleSeed
  )
  const subcategory = normalizeSubcategory(raw.subcategory || metafields.gender || '')
  const color = normalizeColorValue(metafields.color || sellerFacts.color)
  const material = normalizeMaterialValue(metafields.material || sellerFacts.material, color)
  const karat = normalizeSingleLine(metafields.karat || detectedInfo.karat || sellerFacts.karat)
  const stoneType = normalizeStoneType(metafields.stone_type || firstDetectedStone(detectedInfo.stones) || sellerFacts.stoneType)
  const productType = normalizeSingleLine(
    raw.productType || buildFallbackProductType({
      category,
      material,
      stoneType,
      karat,
      color,
      title: sellerFacts.titleSeed || raw.title
    }),
    20
  )
  const title = composeListingTitle({
    rawTitle: raw.title,
    category,
    material,
    stoneType,
    karat,
    color,
    productType,
    sellerFacts
  })
  const weightResult = resolveListingWeight(raw.weight || metafields.weight || detectedInfo.estimatedWeight || '', sellerFacts)
  const stoneCaratResult = resolveStoneCarat(metafields.stone_carat || '', sellerFacts)
  const weight = weightResult.value
  const stoneCarat = stoneCaratResult.value
  const ringSize = normalizeSingleLine(metafields.ring_size)
  const size = normalizeSingleLine(metafields.size || ringSize)
  const description = buildListingDescription({
    title,
    category,
    material,
    stoneType,
    karat,
    color,
    stoneCarat,
    weight,
    ringSize,
    sellerFacts
  })

  if (!title || !description || !productType) {
    throw new Error('Claude returned incomplete product details. Try again with the same photo and information.')
  }

  const metaTitle = clampText(
    buildFallbackMetaTitle({ title, productType, material, stoneType, karat, color }),
    60
  )
  const metaDescription = clampText(
    buildFallbackMetaDescription({ title, material, category, stoneType, stoneCarat, sellerFacts }),
    160
  )
  const urlSlug = normalizeUrlSlug(raw.urlSlug || title)
  const normalizedGender = normalizeGenderValue(metafields.gender || subcategory)
  const gtin = normalizeDigits(metafields.gtin)
  const mpn = normalizeSingleLine(metafields.mpn)
  const tags = normalizeListingTags(raw.tags, {
    title,
    category,
    subcategory,
    material,
    karat,
    color,
    stoneType,
    stoneCarat,
    productType,
    sellerFacts
  })
  const detectedStones = normalizeDetectedStoneList(detectedInfo.stones, sellerFacts, stoneType)

  const normalized = {
    ...raw,
    title,
    description,
    metaTitle,
    metaDescription,
    urlSlug,
    tags,
    category,
    subcategory,
    productType,
    skuCategory,
    weight,
    metafields: {
      ...metafields,
      product_type: normalizeSingleLine(
        metafields.product_type || productType || CATEGORY_DISPLAY_LABELS[category] || 'Jewelry'
      ) || 'Jewelry',
      brand: String(metafields.brand || BRAND_VENDOR).trim() || BRAND_VENDOR,
      material,
      gender: normalizedGender,
      age_group: String(metafields.age_group || 'adult').trim() || 'adult',
      color,
      size,
      weight,
      weight_unit: weight ? (String(metafields.weight_unit || 'g').trim() || 'g') : '',
      condition: String(metafields.condition || 'new').trim() || 'new',
      gtin,
      mpn,
      stone_type: stoneType,
      stone_carat: stoneCarat,
      karat,
      ring_size: ringSize
    },
    detectedInfo: {
      metalType: normalizeSingleLine(detectedInfo.metalType || material),
      karat,
      estimatedWeight: weight || normalizeSingleLine(detectedInfo.estimatedWeight),
      stones: detectedStones,
      style: normalizeSingleLine(detectedInfo.style || sellerFacts.style),
      occasion: Array.isArray(detectedInfo.occasion)
        ? detectedInfo.occasion.map((item) => String(item || '').trim()).filter(Boolean)
        : []
    },
    reviewFlags: [],
    missingFields: []
  }

  if (!normalized.metafields.weight && normalized.weight) {
    normalized.metafields.weight = normalized.weight
  }

  const mergedReviewFlags = mergeReviewFlags(
    normalizeReviewFlags(raw.reviewFlags),
    weightResult.reviewFlags,
    stoneCaratResult.reviewFlags,
    buildComputedListingReviewFlags(normalized)
  )
  normalized.reviewFlags = mergedReviewFlags
  normalized.missingFields = Array.from(new Set([
    ...normalizeMissingFields(raw.missingFields),
    ...mergedReviewFlags.map((item) => item.field).filter(Boolean)
  ]))

  return normalized
}

function normalizeListingTags(value, fallbackContext = {}) {
  const list = Array.isArray(value)
    ? value
    : String(value || '').split(',')

  const directTags = list
    .map((item) => normalizeTagText(item))
    .filter(Boolean)
  const factTags = buildFallbackListingTags(fallbackContext)
  const merged = []
  const seen = new Set()

  for (const tag of [...factTags, ...directTags]) {
    const normalized = normalizeTagText(tag)
    if (!normalized || normalized === 'other' || seen.has(normalized)) continue
    seen.add(normalized)
    merged.push(normalized)
    if (merged.length >= 12) break
  }

  return merged
}

function buildFallbackListingTags(context = {}) {
  const tags = []
  const addTag = (value) => {
    const normalized = normalizeTagText(value)
    if (!normalized || tags.includes(normalized)) return
    tags.push(normalized)
  }

  const categoryLabel = String(CATEGORY_DISPLAY_LABELS[context.category] || '').toLowerCase()
  const materialPhrase = buildMaterialToken(context.material, context.karat, context.color)
  const sellerTitle = context.sellerFacts?.titleSeed || ''
  const style = context.sellerFacts?.style || ''
  const stoneType = String(context.stoneType || '').toLowerCase()
  const totalStoneCount = context.sellerFacts?.totalStoneCount || 0
  const stoneGroups = Array.isArray(context.sellerFacts?.stoneGroups) ? context.sellerFacts.stoneGroups : []

  addTag(sellerTitle)
  addTag(context.title)
  addTag(style && categoryLabel ? `${style} ${categoryLabel}` : style)
  addTag(materialPhrase)
  addTag(materialPhrase && categoryLabel ? `${materialPhrase} ${categoryLabel}` : '')
  addTag(stoneType && categoryLabel ? `${stoneType} ${categoryLabel}` : '')
  addTag(totalStoneCount > 1 && categoryLabel ? `multi stone ${categoryLabel}` : '')
  addTag(totalStoneCount > 0 && stoneType ? `${totalStoneCount} ${stoneType}${totalStoneCount === 1 ? '' : 's'}` : '')
  addTag(totalStoneCount > 0 && categoryLabel ? `${totalStoneCount} stone ${categoryLabel}` : '')
  addTag(context.stoneCarat ? `${context.stoneCarat} ct ${stoneType || 'stone'}` : '')
  addTag(context.subcategory)

  for (const group of stoneGroups) {
    addTag(`${formatStoneGroupDimensions(group)} ${stoneType || 'stones'}`)
    if (tags.length >= 12) break
  }

  if (tags.length < 12) {
    addTag(context.productType)
    addTag(context.category)
  }

  return tags.slice(0, 12)
}

function normalizeTagText(value) {
  return normalizeSingleLine(value, 40).toLowerCase()
}

function normalizeSubcategory(value) {
  const normalized = String(value || '').toLowerCase().trim()
  if (normalized === 'mens' || normalized === 'men' || normalized === 'male') return 'mens'
  if (normalized === 'womens' || normalized === 'women' || normalized === 'female') return 'womens'
  if (normalized === 'unisex') return 'unisex'
  return ''
}

function normalizeSingleLine(value, maxLen = 120) {
  return String(value || '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, maxLen)
}

function clampText(value, maxLen) {
  const text = normalizeSingleLine(value, maxLen + 20)
  if (text.length <= maxLen) return text
  return text.slice(0, maxLen).replace(/\s+\S*$/, '').trim()
}

function normalizeUrlSlug(value) {
  return String(value || '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80)
}

function normalizeDigits(value) {
  return String(value || '').replace(/\D/g, '')
}

function normalizeDescriptionHtml(value) {
  const text = String(value || '').trim()
  if (!text) return ''
  if (/<[a-z][\s\S]*>/i.test(text)) return text
  return `<p>${escapeHtml(text)}</p>`
}

function escapeHtml(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function normalizeGenderValue(value) {
  const normalized = String(value || '').toLowerCase().trim()
  if (normalized === 'mens' || normalized === 'men' || normalized === 'male') return 'male'
  if (normalized === 'womens' || normalized === 'women' || normalized === 'female') return 'female'
  if (normalized === 'unisex') return 'unisex'
  return ''
}

function normalizeStoneType(value) {
  const text = normalizeSingleLine(value, 80)
  if (!text) return ''
  const lower = text.toLowerCase()
  if (/diamond|diamante|\bdia\b/.test(lower)) return 'Diamond'
  if (/cubic zirconia|\bcz\b|zirconia/.test(lower)) return 'Cubic Zirconia'
  if (/emerald|esmeralda/.test(lower)) return 'Emerald'
  if (/ruby|rubi/.test(lower)) return 'Ruby'
  if (/sapphire|safira/.test(lower)) return 'Sapphire'
  if (/moissanite/.test(lower)) return 'Moissanite'
  return toTitleCase(text.replace(/\bdiamonds?\b/ig, 'Diamond'))
}

function normalizeColorValue(value) {
  const text = normalizeSingleLine(value, 40)
  if (!text) return ''
  const lower = text.toLowerCase()
  if (/white|branco/.test(lower)) return 'White'
  if (/yellow|amarelo/.test(lower)) return 'Yellow'
  if (/rose|rosa|pink/.test(lower)) return 'Rose'
  if (/black|preto/.test(lower)) return 'Black'
  if (/silver|prata/.test(lower)) return 'Silver'
  return toTitleCase(text)
}

function normalizeMaterialValue(value, colorHint = '') {
  const text = normalizeSingleLine(value, 80)
  const lower = text.toLowerCase()
  const normalizedColor = normalizeColorValue(colorHint)

  if (/white gold|ouro branco/.test(lower) || (normalizedColor === 'White' && /(gold|ouro)/.test(lower))) {
    return 'White Gold'
  }
  if (/yellow gold|ouro amarelo/.test(lower) || (normalizedColor === 'Yellow' && /(gold|ouro)/.test(lower))) {
    return 'Yellow Gold'
  }
  if (/rose gold|pink gold|ouro ros/.test(lower) || (normalizedColor === 'Rose' && /(gold|ouro)/.test(lower))) {
    return 'Rose Gold'
  }
  if (/(gold|ouro)/.test(lower)) return 'Gold'
  if (/(silver|prata)/.test(lower)) return 'Silver'
  if (/(platinum|platina)/.test(lower)) return 'Platinum'

  return toTitleCase(text)
}

function firstDetectedStone(stones) {
  if (!Array.isArray(stones)) return ''
  return normalizeSingleLine(stones.find(Boolean) || '')
}

function normalizeDetectedStoneList(stones, sellerFacts, stoneType = '') {
  const direct = Array.isArray(stones)
    ? stones.map((item) => normalizeSingleLine(item, 120)).filter(Boolean)
    : []
  const fromGroups = Array.isArray(sellerFacts?.stoneGroups)
    ? sellerFacts.stoneGroups.map((group) => buildStoneGroupSpec(group, stoneType)).filter(Boolean)
    : []
  const merged = []
  const seen = new Set()

  for (const item of [...fromGroups, ...direct]) {
    const normalized = normalizeSingleLine(item, 120)
    if (!normalized || seen.has(normalized.toLowerCase())) continue
    seen.add(normalized.toLowerCase())
    merged.push(normalized)
  }

  return merged
}

function buildFallbackProductType({ category, material, stoneType, karat, color, title }) {
  const itemToken = CATEGORY_DISPLAY_LABELS[category] || ''
  const stoneLabel = [normalizeSingleLine(stoneType), itemToken].filter(Boolean).join(' ')
  const materialToken = [buildMaterialToken(material, karat, color), itemToken].filter(Boolean).join(' ')
  return clampText(stoneLabel || materialToken || normalizeSingleLine(title, 20), 20)
}

function composeListingTitle({ rawTitle, category, material, stoneType, karat, color, productType, sellerFacts }) {
  const aiTitle = normalizeSingleLine(rawTitle, 140)
  const sellerTitle = normalizeSingleLine(sellerFacts?.titleSeed, 140)
  let chosenTitle = aiTitle

  if (sellerTitle) {
    const sellerScore = scoreListingTitle(sellerTitle, sellerFacts)
    const aiScore = scoreListingTitle(aiTitle, sellerFacts)
    if (!chosenTitle || isWeakListingTitle(aiTitle, sellerFacts) || sellerScore > aiScore) {
      chosenTitle = sellerTitle
    }
  }

  if (!chosenTitle || isWeakListingTitle(chosenTitle, sellerFacts)) {
    chosenTitle = buildFallbackTitle({ category, material, stoneType, karat, color, productType, sellerFacts })
  }

  const materialToken = buildMaterialToken(material, karat, color)
  if (materialToken && !containsLoosePhrase(chosenTitle, materialToken)) {
    chosenTitle = `${materialToken} ${chosenTitle}`
  }

  return clampText(toTitleCase(chosenTitle), 140)
}

function scoreListingTitle(title, sellerFacts = {}) {
  const normalized = normalizeSingleLine(title, 140)
  if (!normalized) return 0

  let score = normalized.split(/\s+/).length
  if (!isWeakListingTitle(normalized, sellerFacts)) score += 3
  if (sellerFacts?.titleSeed && containsLoosePhrase(normalized, sellerFacts.titleSeed)) score += 4
  if (sellerFacts?.style && containsLoosePhrase(normalized, sellerFacts.style)) score += 2
  if (/(gold|silver|platinum|white|yellow|rose|diamond)/i.test(normalized)) score += 2
  return score
}

function isWeakListingTitle(title, sellerFacts = {}) {
  const normalized = normalizeSingleLine(title, 140).toLowerCase()
  if (!normalized) return true
  if (normalized.split(/\s+/).length < 2) return true
  if (/\bwith\s+\d+\s+(stone|stones|diamond|diamonds|gem|gems)\b/.test(normalized)) return true
  if (/^(diamond|gold|silver|ring|bracelet|pendant|chain|necklace|earrings?|watch|jewelry)\b/.test(normalized)
    && !containsLoosePhrase(normalized, sellerFacts.titleSeed || '')) {
    return true
  }
  return false
}

function containsLoosePhrase(target, phrase) {
  const targetText = normalizeSingleLine(target, 160).toLowerCase()
  const phraseText = normalizeSingleLine(phrase, 160).toLowerCase()
  if (!targetText || !phraseText) return false
  return targetText.includes(phraseText)
}

function buildFallbackTitle({ category, material, stoneType, karat, color, productType, sellerFacts }) {
  return clampText(
    [
      buildMaterialToken(material, karat, color),
      sellerFacts?.style,
      normalizeSingleLine(stoneType),
      CATEGORY_DISPLAY_LABELS[category] || normalizeSingleLine(productType)
    ].filter(Boolean).join(' '),
    140
  )
}

function buildMaterialToken(material, karat, color = '') {
  const normalizedColor = normalizeColorValue(color)
  let normalizedMaterial = normalizeMaterialValue(material, normalizedColor)

  if (!normalizedMaterial && normalizedColor && /white|yellow|rose/i.test(normalizedColor)) {
    normalizedMaterial = `${normalizedColor} Gold`
  }

  if (!normalizedMaterial) {
    const normalizedKarat = normalizeSingleLine(karat)
    return normalizedKarat ? `${normalizedKarat}K Gold` : ''
  }

  const normalizedKarat = extractKarat(karat)
  const hasKarat = /\b\d{1,2}k\b/i.test(normalizedMaterial)
  return normalizeSingleLine(`${normalizedKarat && !hasKarat ? `${normalizedKarat}K ` : ''}${normalizedMaterial}`)
}

function buildListingDescription({ title, category, material, stoneType, karat, color, stoneCarat, weight, ringSize, sellerFacts }) {
  const titleText = title || 'Jewelry Item'
  const materialToken = buildMaterialToken(material, karat, color)
  const stoneSummary = buildStoneSummaryText({ stoneType, stoneCarat, sellerFacts })
  const intro = stoneSummary
    ? `${titleText} features ${stoneSummary}.`
    : `${titleText} is a ${String(CATEGORY_DISPLAY_LABELS[category] || 'jewelry piece').toLowerCase()} from ${BRAND_VENDOR}.`

  const secondBits = []
  if (materialToken && !containsLoosePhrase(titleText, materialToken)) {
    secondBits.push(`Metal details include ${materialToken}`)
  }
  if (sellerFacts?.stoneGroups?.length) {
    secondBits.push(`confirmed stone measurements across ${sellerFacts.stoneGroups.length} size group${sellerFacts.stoneGroups.length === 1 ? '' : 's'}`)
  }
  if (stoneCarat) {
    secondBits.push(`estimated total stone carat of ${stoneCarat} ct`)
  }
  if (weight) {
    secondBits.push(`listed weight of ${weight} g`)
  }
  if (ringSize) {
    secondBits.push(`ring size ${ringSize}`)
  }
  const secondParagraph = secondBits.length
    ? `Confirmed details include ${secondBits.join(', ')}.`
    : `This listing focuses on confirmed product details, visible stone layout, and seller-provided measurements.`

  const specs = buildDescriptionSpecs({ materialToken, stoneType, stoneCarat, weight, ringSize, sellerFacts })
  const faqItems = buildListingFaq({ title: titleText, materialToken, stoneType, stoneCarat, weight, sellerFacts, category })

  return normalizeDescriptionHtml([
    `<p>${escapeHtml(intro)}</p>`,
    `<p>${escapeHtml(secondParagraph)}</p>`,
    specs.length ? `<ul>${specs.map((item) => `<li>${escapeHtml(item)}</li>`).join('')}</ul>` : '',
    faqItems.length ? `<h3>Q&A</h3><ul>${faqItems.map((item) => `<li><strong>${escapeHtml(item.question)}</strong> ${escapeHtml(item.answer)}</li>`).join('')}</ul>` : ''
  ].filter(Boolean).join(''))
}

function buildDescriptionSpecs({ materialToken, stoneType, stoneCarat, weight, ringSize, sellerFacts }) {
  const specs = []

  if (materialToken) specs.push(`Metal: ${materialToken}`)
  if (sellerFacts?.totalStoneCount && stoneType) {
    specs.push(`Stones: ${sellerFacts.totalStoneCount} ${pluralizeStone(stoneType, sellerFacts.totalStoneCount)}`)
  } else if (stoneType) {
    specs.push(`Stone Type: ${stoneType}`)
  }
  for (const group of sellerFacts?.stoneGroups || []) {
    specs.push(buildStoneGroupSpec(group, stoneType))
  }
  if (stoneCarat) specs.push(`Estimated Total Stone Carat: ${stoneCarat} ct`)
  if (weight) specs.push(`Weight: ${weight} g`)
  if (ringSize) specs.push(`Ring Size: ${ringSize}`)

  return specs.slice(0, 8)
}

function buildListingFaq({ title, materialToken, stoneType, stoneCarat, weight, sellerFacts, category }) {
  const items = []
  const categoryLabel = String(CATEGORY_DISPLAY_LABELS[category] || 'jewelry').toLowerCase()
  const stoneSummary = buildStoneSummaryText({ stoneType, stoneCarat, sellerFacts })

  items.push({
    question: `What type of ${categoryLabel} is this?`,
    answer: `This piece is listed as ${title}.`
  })

  if (stoneSummary) {
    items.push({
      question: 'What stone details are confirmed?',
      answer: `Confirmed details include ${stoneSummary}.`
    })
  }

  if (sellerFacts?.stoneGroups?.length) {
    items.push({
      question: 'What stone sizes are listed?',
      answer: sellerFacts.stoneGroups.map((group) => buildStoneGroupAnswer(group, stoneType)).join('; ') + '.'
    })
  } else if (materialToken) {
    items.push({
      question: 'What metal is listed?',
      answer: materialToken + '.'
    })
  }

  if (items.length < 3 && stoneCarat) {
    items.push({
      question: 'Is total carat noted in the draft?',
      answer: `The draft includes an estimated total of ${stoneCarat} ct based on the listed stone measurements.`
    })
  } else if (items.length < 3 && weight) {
    items.push({
      question: 'Is product weight included?',
      answer: `The listed weight is ${weight} g.`
    })
  }

  return items.slice(0, 3)
}

function buildStoneSummaryText({ stoneType, stoneCarat, sellerFacts }) {
  const totalStoneCount = sellerFacts?.totalStoneCount || 0
  const groupCount = sellerFacts?.stoneGroups?.length || 0
  const normalizedStoneType = normalizeStoneType(stoneType)

  if (totalStoneCount && normalizedStoneType) {
    const pieces = [`${totalStoneCount} ${pluralizeStone(normalizedStoneType, totalStoneCount)}`]
    if (groupCount > 1) {
      pieces.push(`across ${groupCount} size groups`)
    }
    if (stoneCarat) {
      pieces.push(`with an estimated total of ${stoneCarat} ct`)
    }
    return pieces.join(' ')
  }

  if (normalizedStoneType && stoneCarat) {
    return `${stoneCarat} ct of ${pluralizeStone(normalizedStoneType, 2)}`
  }

  if (normalizedStoneType) return normalizedStoneType.toLowerCase()
  return ''
}

function pluralizeStone(stoneType, count) {
  const normalized = normalizeStoneType(stoneType)
  if (!normalized) return count === 1 ? 'stone' : 'stones'
  if (count === 1) return normalized.toLowerCase()
  if (/ruby$/i.test(normalized)) return 'rubies'
  return `${normalized.toLowerCase()}s`
}

function buildStoneGroupSpec(group, stoneType) {
  if (!group?.count || !group?.lengthMm || !group?.widthMm) return ''
  const stoneLabel = pluralizeStone(stoneType || 'stone', group.count)
  return `${group.count} ${stoneLabel}: ${formatStoneGroupDimensions(group)}`
}

function buildStoneGroupAnswer(group, stoneType) {
  if (!group?.count || !group?.lengthMm || !group?.widthMm) return ''
  const stoneLabel = pluralizeStone(stoneType || 'stone', group.count)
  return `${group.count} ${stoneLabel} measure ${formatStoneGroupDimensions(group)}`
}

function formatStoneGroupDimensions(group) {
  if (!group?.lengthMm || !group?.widthMm) return ''
  return `${formatMetricToken(group.lengthMm)} x ${formatMetricToken(group.widthMm)} mm`
}

function buildFallbackMetaTitle({ title, productType, material, stoneType, karat, color }) {
  const materialToken = buildMaterialToken(material, karat, color)
  const base = title
    || [materialToken, stoneType || productType].filter(Boolean).join(' ')
  return clampText([base, BRAND_VENDOR].filter(Boolean).join(' | '), 60)
}

function buildFallbackMetaDescription({ title, material, category, stoneType, stoneCarat, sellerFacts }) {
  const categoryLabel = String(CATEGORY_DISPLAY_LABELS[category] || 'Jewelry').toLowerCase()
  const materialToken = buildMaterialToken(material, sellerFacts?.karat || '', sellerFacts?.color || '')
  const details = []

  if (sellerFacts?.totalStoneCount && stoneType) {
    details.push(`${sellerFacts.totalStoneCount} ${pluralizeStone(stoneType, sellerFacts.totalStoneCount)}`)
  }
  if (sellerFacts?.stoneGroups?.length) {
    details.push(sellerFacts.stoneGroups.map((group) => buildStoneGroupSpec(group, stoneType)).filter(Boolean).join(', '))
  }
  if (stoneCarat) {
    details.push(`estimated ${stoneCarat} ct total`)
  }

  return [
    `Shop ${normalizeSingleLine(title || `${materialToken} ${categoryLabel}`.trim(), 100)}.`,
    details.length ? `Confirmed details: ${details.join('; ')}.` : '',
    `${BRAND_VENDOR}.`
  ].filter(Boolean).join(' ')
}

function extractSellerFacts(description) {
  const text = normalizeSingleLine(description, 1200)
  const category = normalizeListingCategory(text)
  const titleSeed = extractSellerTitleSeed(text)
  const color = detectMetalColorFromText(text)
  const material = detectMaterialFromText(text, color)
  const karat = detectKaratFromText(text)
  const stoneType = detectStoneTypeFromText(text)
  const stoneGroups = extractStoneGroups(text)
  const explicitWeight = extractExplicitWeightFromText(text)
  const explicitStoneCarat = extractExplicitStoneCaratFromText(text)
  const style = extractSellerStyle(titleSeed, category)

  return {
    raw: text,
    titleSeed,
    style,
    category,
    color,
    material,
    karat,
    stoneType,
    stoneGroups,
    totalStoneCount: stoneGroups.reduce((sum, item) => sum + item.count, 0),
    explicitWeight,
    explicitStoneCarat,
    dimensionValues: stoneGroups.flatMap((item) => [item.lengthMm, item.widthMm]).map((value) => Number(value))
  }
}

function extractSellerTitleSeed(text) {
  const normalized = normalizeSingleLine(text, 200)
  if (!normalized) return ''

  let seed = normalized.split(/[\n.;]/)[0] || normalized
  seed = seed.split(',')[0] || seed
  seed = seed.replace(/\b\d+\s*(?:diamonds?|diamantes?|dia|stones?|gems?)\b[\s\S]*$/i, '').trim()
  seed = seed.replace(/\b\d+(?:[.,]\d+)?\s*mm\b[\s\S]*$/i, '').trim()
  seed = seed.replace(/[-|/]+$/, '').trim()

  const looksMetalTagged = /(?:au\s*)?(10|14|18|22|24)\s*(?:k|kt)\b|gold|ouro|white gold|yellow gold|rose gold|ouro branco|ouro amarelo|ouro ros/i.test(seed)
  seed = seed.replace(/\b(?:au\s*)?(10|14|18|22|24)\s*(?:k|kt)\b/ig, '').trim()
  seed = seed.replace(/\b(?:white gold|yellow gold|rose gold|pink gold|ouro branco|ouro amarelo|ouro ros[ae]?|gold|ouro)\b/ig, '').trim()
  if (looksMetalTagged) {
    seed = seed.replace(/\b(?:white|yellow|rose|pink|branco|amarelo|rosa)\b/ig, '').trim()
  }
  seed = translateSellerTitleSeed(seed)

  if (!seed) return ''
  if (!/(ring|bracelet|bangle|chain|necklace|earrings?|pendant|watch|anel|pulseira|corrente|colar|brinco|pingente|diamond|gold|ouro|half round|white gold|yellow gold|rose gold|branco|amarelo|rose)/i.test(seed)) {
    return ''
  }

  return toTitleCase(seed)
}

function extractSellerStyle(titleSeed, category) {
  const seed = normalizeSingleLine(titleSeed, 80)
  if (!seed) return ''

  const categoryPattern = String(CATEGORY_DISPLAY_LABELS[category] || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  let style = seed
    .replace(/\b\d{1,2}k\b/ig, '')
    .replace(/\b(?:white|yellow|rose)\s+gold\b/ig, '')
    .replace(/\b(?:gold|silver|platinum|diamond)\b/ig, '')

  if (categoryPattern) {
    style = style.replace(new RegExp(`\\b${categoryPattern}\\b`, 'ig'), '')
  }

  return clampText(normalizeSingleLine(style), 30)
}

function translateSellerTitleSeed(seed) {
  return normalizeSingleLine(seed, 120)
    .replace(/\bmeia cana\b/ig, 'half round')
    .replace(/\bdiamantes?\b/ig, 'diamond')
    .replace(/\banel\b/ig, 'ring')
    .replace(/\bpulseira\b/ig, 'bracelet')
    .replace(/\bcorrente\b/ig, 'chain')
    .replace(/\bcolar\b/ig, 'necklace')
    .replace(/\bbrincos?\b/ig, 'earrings')
    .replace(/\bpingente\b/ig, 'pendant')
}

function toTitleCase(value) {
  return normalizeSingleLine(value, 160)
    .toLowerCase()
    .replace(/\b[a-z]/g, (char) => char.toUpperCase())
    .replace(/\b(\d{1,2})k\b/g, (_, digits) => `${digits}K`)
    .replace(/\bau\b/g, 'AU')
    .replace(/\bcz\b/g, 'CZ')
}

function detectStoneTypeFromText(text) {
  const lower = String(text || '').toLowerCase()
  if (/diamond|diamante|\bdia\b/.test(lower)) return 'Diamond'
  if (/cubic zirconia|\bcz\b|zirconia/.test(lower)) return 'Cubic Zirconia'
  if (/emerald|esmeralda/.test(lower)) return 'Emerald'
  if (/ruby|rubi/.test(lower)) return 'Ruby'
  if (/sapphire|safira/.test(lower)) return 'Sapphire'
  if (/moissanite/.test(lower)) return 'Moissanite'
  return ''
}

function detectMaterialFromText(text, color) {
  const lower = String(text || '').toLowerCase()
  if (/white gold|ouro branco/.test(lower) || (color === 'White' && /(gold|ouro)/.test(lower))) return 'White Gold'
  if (/yellow gold|ouro amarelo/.test(lower) || (color === 'Yellow' && /(gold|ouro)/.test(lower))) return 'Yellow Gold'
  if (/rose gold|pink gold|ouro ros/.test(lower) || (color === 'Rose' && /(gold|ouro)/.test(lower))) return 'Rose Gold'
  if (/(gold|ouro)/.test(lower)) return 'Gold'
  if (/(silver|prata)/.test(lower)) return 'Silver'
  if (/(platinum|platina)/.test(lower)) return 'Platinum'
  return ''
}

function detectKaratFromText(text) {
  const match = String(text || '').match(/\b(?:au\s*)?(10|14|18|22|24)\s*(?:k|kt)\b/i)
  return match ? match[1] : ''
}

function detectMetalColorFromText(text) {
  const lower = String(text || '').toLowerCase()
  if (/white|branco/.test(lower)) return 'White'
  if (/yellow|amarelo/.test(lower)) return 'Yellow'
  if (/rose|rosa|pink/.test(lower)) return 'Rose'
  return ''
}

function extractStoneGroups(text) {
  const regex = /(\d+)\s*(?:diamonds?|diamantes?|dia|stones?|gems?|cz)\s*(?:measuring|measure|measures|sized|size)?\s*(\d+(?:[.,]\d+)?)\s*(?:mm)?\s*[xX]\s*(\d+(?:[.,]\d+)?)\s*(?:mm)?/gi
  const groups = []
  const seen = new Set()

  for (const match of String(text || '').matchAll(regex)) {
    const count = Number.parseInt(match[1], 10)
    const lengthMm = Number.parseFloat(String(match[2] || '').replace(',', '.'))
    const widthMm = Number.parseFloat(String(match[3] || '').replace(',', '.'))
    if (!Number.isFinite(count) || !Number.isFinite(lengthMm) || !Number.isFinite(widthMm)) continue

    const key = `${count}-${lengthMm}-${widthMm}`
    if (seen.has(key)) continue
    seen.add(key)
    groups.push({ count, lengthMm, widthMm })
  }

  return groups
}

function extractExplicitWeightFromText(text) {
  const match = String(text || '').match(/\b(\d+(?:[.,]\d+)?)\s*(?:g|gram|grams)\b/i)
  return match ? normalizeWeightToken(match[1]) : ''
}

function extractExplicitStoneCaratFromText(text) {
  const match = String(text || '').match(/\b(\d+(?:[.,]\d+)?)\s*(?:ct|cts|ctw|tcw|carat|carats)\b/i)
  return match ? normalizeWeightToken(match[1]) : ''
}

function resolveListingWeight(value, sellerFacts = {}) {
  const explicitWeight = normalizeWeightToken(sellerFacts.explicitWeight)
  if (explicitWeight) {
    return { value: explicitWeight, reviewFlags: [] }
  }

  const normalized = normalizeWeightToken(value)
  if (!normalized) {
    return { value: '', reviewFlags: [] }
  }

  const numericValue = Number.parseFloat(normalized)
  const matchesStoneMeasurement = Array.isArray(sellerFacts.dimensionValues)
    && sellerFacts.dimensionValues.some((dimension) => Number.isFinite(dimension) && Math.abs(dimension - numericValue) < 0.05)

  if (matchesStoneMeasurement) {
    return {
      value: '',
      reviewFlags: [{
        field: 'weight',
        label: formatReviewField('weight'),
        reason: 'Removed a suspicious weight because it matched a stone millimeter measurement. Confirm grams manually.',
        severity: 'required'
      }]
    }
  }

  return { value: normalized, reviewFlags: [] }
}

function resolveStoneCarat(value, sellerFacts = {}) {
  const explicitStoneCarat = normalizeWeightToken(sellerFacts.explicitStoneCarat)
  if (explicitStoneCarat) {
    return { value: explicitStoneCarat, reviewFlags: [] }
  }

  const normalized = normalizeWeightToken(value)
  const estimatedStoneCarat = estimateStoneCaratFromGroups(sellerFacts.stoneGroups, sellerFacts.stoneType)
  const reviewFlag = {
    field: 'metafields.stone_carat',
    label: formatReviewField('metafields.stone_carat'),
    reason: 'Stone carat is estimated from seller-provided stone counts and millimeter measurements. Verify total CT before publish.',
    severity: 'recommended'
  }

  if (normalized) {
    return {
      value: normalized,
      reviewFlags: sellerFacts.stoneGroups?.length ? [reviewFlag] : []
    }
  }

  if (estimatedStoneCarat) {
    return {
      value: estimatedStoneCarat,
      reviewFlags: [reviewFlag]
    }
  }

  return { value: '', reviewFlags: [] }
}

function estimateStoneCaratFromGroups(groups, stoneType) {
  if (!Array.isArray(groups) || groups.length === 0) return ''
  if (normalizeStoneType(stoneType) !== 'Diamond') return ''

  let total = 0
  for (const group of groups) {
    const length = Number(group.lengthMm)
    const width = Number(group.widthMm)
    const count = Number(group.count)
    if (!Number.isFinite(length) || !Number.isFinite(width) || !Number.isFinite(count)) continue
    total += length * width * Math.min(length, width) * 0.00366 * count
  }

  if (!Number.isFinite(total) || total <= 0.03) return ''
  return formatMetricToken(total, 2)
}

function formatMetricToken(value, decimals = 2) {
  const numericValue = Number(value)
  if (!Number.isFinite(numericValue) || numericValue <= 0) return ''
  if (Number.isInteger(numericValue)) return `${numericValue}`
  return numericValue.toFixed(decimals).replace(/\.?0+$/, '')
}

function normalizeReviewFlags(value) {
  if (!Array.isArray(value)) return []
  return value
    .map((item) => {
      if (typeof item === 'string') {
        const field = normalizeReviewField(item)
        if (!field) return null
        return {
          field,
          label: formatReviewField(field),
          reason: 'Needs manual review.',
          severity: 'recommended'
        }
      }
      if (!item || typeof item !== 'object') return null
      const field = normalizeReviewField(item.field || item.key || item.id)
      if (!field) return null
      return {
        field,
        label: formatReviewField(field),
        reason: normalizeSingleLine(item.reason || item.message || 'Needs manual review.', 220),
        severity: String(item.severity || '').toLowerCase() === 'required' ? 'required' : 'recommended'
      }
    })
    .filter(Boolean)
}

function normalizeMissingFields(value) {
  if (!Array.isArray(value)) return []
  return Array.from(new Set(value.map((item) => normalizeReviewField(item)).filter(Boolean)))
}

function normalizeReviewField(value) {
  const raw = String(value || '').trim()
  if (!raw) return ''
  if (LISTING_FIELD_LABELS[raw]) return raw
  if (LISTING_FIELD_LABELS[`metafields.${raw}`]) return `metafields.${raw}`
  return raw
}

function formatReviewField(field) {
  const normalized = normalizeReviewField(field)
  return LISTING_FIELD_LABELS[normalized]
    || normalized.replace(/^metafields\./, '').replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase())
}

function mergeReviewFlags(...lists) {
  const merged = new Map()
  for (const list of lists) {
    for (const item of list || []) {
      if (!item?.field) continue
      const existing = merged.get(item.field)
      if (!existing) {
        merged.set(item.field, item)
        continue
      }
      merged.set(item.field, {
        ...existing,
        ...item,
        severity: existing.severity === 'required' || item.severity === 'required' ? 'required' : 'recommended',
        reason: item.reason && item.reason.length >= existing.reason.length ? item.reason : existing.reason
      })
    }
  }
  return Array.from(merged.values())
}

function buildComputedListingReviewFlags(payload) {
  const flags = []

  if (!payload.category || payload.category === 'other') {
    flags.push({
      field: 'category',
      label: formatReviewField('category'),
      reason: 'Confirm the exact jewelry category before publish.',
      severity: 'required'
    })
  }

  if (!payload.metafields?.material) {
    flags.push({
      field: 'metafields.material',
      label: formatReviewField('metafields.material'),
      reason: 'Material is not clearly confirmed from the image or seller description.',
      severity: 'required'
    })
  }

  if (looksLikeGoldProduct(payload) && !payload.metafields?.karat) {
    flags.push({
      field: 'metafields.karat',
      label: formatReviewField('metafields.karat'),
      reason: 'Karat is likely important for this item but is not confirmed.',
      severity: 'required'
    })
  }

  if (!payload.weight) {
    flags.push({
      field: 'weight',
      label: formatReviewField('weight'),
      reason: 'Weight is still missing. Add it manually if available.',
      severity: 'recommended'
    })
  }

  if (!payload.subcategory) {
    flags.push({
      field: 'subcategory',
      label: formatReviewField('subcategory'),
      reason: 'Audience is not confirmed. Set mens, womens, or unisex if needed.',
      severity: 'recommended'
    })
  }

  if (!payload.metafields?.color) {
    flags.push({
      field: 'metafields.color',
      label: formatReviewField('metafields.color'),
      reason: 'Color is still empty. Confirm the dominant visible color if useful for search.',
      severity: 'recommended'
    })
  }

  if (payload.metafields?.stone_type && !payload.metafields?.stone_carat) {
    flags.push({
      field: 'metafields.stone_carat',
      label: formatReviewField('metafields.stone_carat'),
      reason: 'Stone carat is still missing. Add or verify total CT if it can be confirmed or estimated safely.',
      severity: 'recommended'
    })
  }

  if (payload.category === 'rings' && !payload.metafields?.ring_size) {
    flags.push({
      field: 'metafields.ring_size',
      label: formatReviewField('metafields.ring_size'),
      reason: 'Ring size is empty. Add it only if confirmed.',
      severity: 'recommended'
    })
  }

  return flags
}

function looksLikeGoldProduct(payload) {
  const haystack = [
    payload?.title,
    payload?.productType,
    payload?.metafields?.material,
    payload?.detectedInfo?.metalType
  ]
    .map((item) => String(item || '').toLowerCase())
    .join(' ')

  return /(gold|ouro|10k|14k|18k|24k)/i.test(haystack)
}

function normalizeListingCategory(value) {
  if (isFragrance({category:value})) return 'perfumes'
  const normalized = toNormalizedKey(value)
  if (normalized.includes('ring') || normalized.includes('anel')) return 'rings'
  if (normalized.includes('chain') || normalized.includes('corrente')) return 'chains'
  if (normalized.includes('bracelet') || normalized.includes('pulseira') || normalized.includes('bangle')) return 'bracelets'
  if (normalized.includes('necklace') || normalized.includes('colar')) return 'necklaces'
  if (normalized.includes('earring') || normalized.includes('brinco')) return 'earrings'
  if (normalized.includes('pendant') || normalized.includes('pingente')) return 'pendants'
  if (normalized.includes('watch') || normalized.includes('relogio') || normalized.includes('relógio')) return 'watches'
  return 'other'
}

function buildUpdateSuggestionsPrompt(payload = {}) {
  if (isFragrance(payload)) {
    return buildFragrancePrompt(`${payload.title || ''}\n${payload.description || ''}`, {
      fragrance: payload.metafields || {}, weight: payload.weight
    })
  }
  const title = safePromptValue(payload.title, 140)
  const description = safePromptValue(payload.description, 1600)
  const productType = safePromptValue(payload.productType, 80)
  const weight = safePromptValue(payload.weight, 40)

  return `You are an expert jewelry catalog editor. Improve this product data for e-commerce and printing.

Input fields:
- title: "${title}"
- description: "${description}"
- productType: "${productType}"
- weight: "${weight}"

Rules:
1) Keep productType exactly as-is. Do not change it.
2) Return better title and description in clean HTML.
3) Return useful SEO tags.
4) Fill jewelry details in metafields when possible: karat, stone_type, stone_carat, weight.
5) Return tagPrintInfo for label printing with max ${TAG_PRINT_INFO_MAX} characters.
6) tagPrintInfo should include:
   - gold type (AU + karat when possible),
   - short item name,
   - stone carat (if stone exists),
   - weight in grams.
7) Keep tagPrintInfo concise and readable for tiny labels.

Return ONLY valid JSON in this exact shape:
{
  "title": "string",
  "description": "html string",
  "tags": ["tag1", "tag2"],
  "weight": "string number in grams",
  "tagPrintInfo": "max ${TAG_PRINT_INFO_MAX} chars",
  "summary": "one short line explaining key improvements",
  "metafields": {
    "karat": "string",
    "stone_type": "string",
    "stone_carat": "string",
    "weight": "string",
    "tag_print_info": "same as tagPrintInfo"
  }
}`
}

function normalizeGoogleMissingFieldKeys(fields) {
  const rawFields = Array.isArray(fields)
    ? fields
    : String(fields || '').split(',')

  const normalized = []
  const seen = new Set()
  for (const item of rawFields) {
    const raw = String(item || '').trim()
    if (!raw) continue
    const key = raw.replace(/^metafields\./, '')
    if (!GOOGLE_METAFIELDS.includes(key) || seen.has(key)) continue
    seen.add(key)
    normalized.push(key)
  }
  return normalized
}

function normalizeAgeGroupValue(value) {
  const normalized = String(value || '').toLowerCase().trim()
  if (!normalized) return ''
  if (/adult|adults/.test(normalized)) return 'adult'
  if (/kid|kids|child|children|youth|teen/.test(normalized)) return 'kids'
  return ''
}

function normalizeConditionValue(value) {
  const normalized = String(value || '').toLowerCase().trim()
  if (!normalized) return ''
  if (/new|novo|nova/.test(normalized)) return 'new'
  if (/used|pre-owned|preowned|pre owned|used/.test(normalized)) return 'used'
  if (/refurbished|refurb/.test(normalized)) return 'refurbished'
  return ''
}

function normalizeWeightUnitValue(value) {
  const normalized = String(value || '').toLowerCase().trim()
  if (!normalized) return ''
  if (normalized === 'g' || normalized === 'gram' || normalized === 'grams') return 'g'
  if (normalized === 'kg' || normalized === 'kilogram' || normalized === 'kilograms') return 'kg'
  if (normalized === 'oz' || normalized === 'ounce' || normalized === 'ounces') return 'oz'
  if (normalized === 'lb' || normalized === 'lbs' || normalized === 'pound' || normalized === 'pounds') return 'lb'
  return ''
}

function normalizeGtinValue(value) {
  const digits = normalizeDigits(value)
  if (digits.length >= 8 && digits.length <= 14) return digits
  return ''
}

function buildDeterministicGoogleFixes(payload = {}) {
  if (isFragrance(payload)) return { productType: 'Perfume', metafields: { ...(payload.metafields || {}), product_type: 'Perfume', brand: payload.metafields?.brand || '', gtin: payload.metafields?.gtin || '', mpn: payload.metafields?.mpn || '' }, reviewFlags: [] }
  const metafields = payload?.metafields && typeof payload.metafields === 'object' && !Array.isArray(payload.metafields)
    ? payload.metafields
    : {}
  const combinedSellerText = [
    payload?.sellerDescription,
    payload?.title,
    stripHtml(payload?.description),
    payload?.productType,
    Array.isArray(payload?.tags) ? payload.tags.join(', ') : payload?.tags
  ]
    .filter(Boolean)
    .join('. ')

  const sellerFacts = extractSellerFacts(combinedSellerText)
  const variantWeight = payload?.variant?.weight || ''
  const effectiveWeight = resolveListingWeight(
    payload?.weight || metafields?.weight || variantWeight,
    sellerFacts
  )
  const color = normalizeColorValue(metafields?.color || sellerFacts.color || '')
  const material = normalizeMaterialValue(metafields?.material || sellerFacts.material || '', color)
  const productType = normalizeSingleLine(
    metafields?.product_type
      || payload?.productType
      || CATEGORY_DISPLAY_LABELS[normalizeListingCategory(payload?.productType || payload?.title || sellerFacts.category)]
      || CATEGORY_DISPLAY_LABELS[sellerFacts.category]
      || '',
    80
  )
  const gtin = normalizeGtinValue(metafields?.gtin || payload?.barcode || payload?.variant?.barcode || '')
  const mpn = normalizeSingleLine(metafields?.mpn || payload?.sku || payload?.variant?.sku || '', 60)
  const size = normalizeSingleLine(metafields?.size || metafields?.ring_size || '', 40)
  const gender = normalizeGenderValue(metafields?.gender || payload?.subcategory || '')

  const resolvedMetafields = {
    product_type: productType,
    brand: normalizeSingleLine(metafields?.brand || payload?.vendor || BRAND_VENDOR, 60) || BRAND_VENDOR,
    material,
    gender,
    age_group: normalizeAgeGroupValue(metafields?.age_group || 'adult') || 'adult',
    color,
    size,
    weight: effectiveWeight.value || '',
    weight_unit: effectiveWeight.value ? (normalizeWeightUnitValue(metafields?.weight_unit || 'g') || 'g') : '',
    condition: normalizeConditionValue(metafields?.condition || 'new') || 'new',
    gtin,
    mpn
  }

  return {
    productType,
    metafields: resolvedMetafields,
    reviewFlags: effectiveWeight.reviewFlags || []
  }
}

function buildGoogleMetadataFixPrompt(payload = {}, context = {}) {
  const metafields = payload?.metafields && typeof payload.metafields === 'object' && !Array.isArray(payload.metafields)
    ? payload.metafields
    : {}
  const deterministic = context?.deterministic?.metafields || {}
  const unresolvedKeys = Array.isArray(context?.unresolvedKeys) ? context.unresolvedKeys : []
  const existingValues = GOOGLE_METAFIELDS
    .map((key) => `- ${key}: "${safePromptValue(metafields[key] || '', 140)}"`)
    .join('\n')
  const deterministicValues = GOOGLE_METAFIELDS
    .filter((key) => deterministic[key])
    .map((key) => `- ${key}: "${safePromptValue(deterministic[key] || '', 140)}"`)
    .join('\n')
  const requestedFieldLines = unresolvedKeys
    .map((key) => `- metafields.${key} (${formatReviewField(`metafields.${key}`)})`)
    .join('\n')

  return `You are fixing missing Google Merchant / marketplace product metadata for an existing ${BRAND_VENDOR} jewelry or perfume product. For perfume, do not infer gold/material from bottle color, use ArtGold as manufacturer, infer age/gender, or use internal SKU/barcode as GTIN/MPN.

Your goal is to fill ONLY the unresolved missing fields using evidence from the current product title, description, tags, barcode, SKU, and image.

Current product:
- title: "${safePromptValue(payload?.title, 180)}"
- productType: "${safePromptValue(payload?.productType, 100)}"
- vendor: "${safePromptValue(payload?.vendor || BRAND_VENDOR, 80)}"
- sku: "${safePromptValue(payload?.sku || payload?.variant?.sku || '', 60)}"
- barcode: "${safePromptValue(payload?.barcode || payload?.variant?.barcode || '', 32)}"
- tags: "${safePromptValue(Array.isArray(payload?.tags) ? payload.tags.join(', ') : payload?.tags, 220)}"
- description: "${safePromptValue(payload?.description, 2200)}"

Existing Google field values:
${existingValues || '- none'}

Deterministic fields already resolved by the app:
${deterministicValues || '- none'}

You must fill only these unresolved fields:
${requestedFieldLines || '- none'}

Rules:
1) Use only evidence that is explicit in the current product data or visible in the image.
2) Do not rewrite the title, description, tags, price, or story copy.
3) Do not invent weight, size, GTIN, or MPN.
4) If barcode already exists, do not change it.
5) If SKU already exists, do not change it.
6) Material should be specific when supported, such as White Gold, Yellow Gold, Rose Gold, Silver, or Platinum.
7) Color should be simple, such as White, Yellow, Rose, Silver, or Black.
8) product_type should be a concise catalog type such as Ring, Pendant, Bracelet, Necklace, Chain, Earrings, Watch, or Wedding Band.
9) gender must be male, female, or unisex.
10) age_group must be adult or kids.
11) condition should be new unless the product data clearly says otherwise.
12) If a field cannot be confirmed, leave it blank and include it in missingFields.

Return ONLY valid JSON in this exact shape:
{
  "productType": "optional concise product type string",
  "summary": "one short line about what was fixed",
  "metafields": {
    "product_type": "",
    "brand": "",
    "material": "",
    "gender": "",
    "age_group": "",
    "color": "",
    "size": "",
    "weight": "",
    "weight_unit": "",
    "condition": "",
    "gtin": "",
    "mpn": ""
  },
  "reviewFlags": [
    {
      "field": "metafields.material",
      "reason": "why manual review is still needed",
      "severity": "required"
    }
  ],
  "missingFields": ["metafields.material"]
}`
}

function normalizeGoogleMetadataFixResponse(raw, context = {}) {
  const payload = context?.payload || {}
  const missingKeys = Array.isArray(context?.missingKeys) ? context.missingKeys : []
  const baseMetafields = raw?.metafields && typeof raw.metafields === 'object' && !Array.isArray(raw.metafields)
    ? raw.metafields
    : {}
  const combinedSellerText = [
    payload?.sellerDescription,
    payload?.title,
    stripHtml(payload?.description),
    payload?.productType,
    Array.isArray(payload?.tags) ? payload.tags.join(', ') : payload?.tags
  ]
    .filter(Boolean)
    .join('. ')
  const sellerFacts = extractSellerFacts(combinedSellerText)

  const color = normalizeColorValue(baseMetafields.color || payload?.metafields?.color || sellerFacts.color || '')
  const materialHint = baseMetafields.material || payload?.metafields?.material || sellerFacts.material || ''
  const resolvedWeight = resolveListingWeight(baseMetafields.weight || '', sellerFacts)

  const normalizedMetafields = {}
  missingKeys.forEach((key) => {
    const rawValue = baseMetafields[key]
    switch (key) {
      case 'product_type':
        normalizedMetafields[key] = normalizeSingleLine(rawValue || raw?.productType || payload?.productType || '', 80)
        break
      case 'brand':
        normalizedMetafields[key] = normalizeSingleLine(rawValue, 60)
        break
      case 'material':
        normalizedMetafields[key] = normalizeMaterialValue(rawValue || materialHint, color)
        break
      case 'gender':
        normalizedMetafields[key] = normalizeGenderValue(rawValue)
        break
      case 'age_group':
        normalizedMetafields[key] = normalizeAgeGroupValue(rawValue)
        break
      case 'color':
        normalizedMetafields[key] = normalizeColorValue(rawValue)
        break
      case 'size':
        normalizedMetafields[key] = normalizeSingleLine(rawValue, 40)
        break
      case 'weight':
        normalizedMetafields[key] = resolvedWeight.value || ''
        break
      case 'weight_unit':
        normalizedMetafields[key] = normalizeWeightUnitValue(rawValue)
        break
      case 'condition':
        normalizedMetafields[key] = normalizeConditionValue(rawValue)
        break
      case 'gtin':
        normalizedMetafields[key] = normalizeGtinValue(rawValue)
        break
      case 'mpn':
        normalizedMetafields[key] = normalizeSingleLine(rawValue, 60)
        break
      default:
        normalizedMetafields[key] = normalizeSingleLine(rawValue, 120)
        break
    }
  })

  if (normalizedMetafields.weight && !normalizedMetafields.weight_unit) {
    normalizedMetafields.weight_unit = 'g'
  }
  if (normalizedMetafields.weight_unit && !normalizedMetafields.weight) {
    normalizedMetafields.weight_unit = ''
  }

  return {
    productType: normalizeSingleLine(raw?.productType || normalizedMetafields.product_type || '', 80),
    summary: normalizeSingleLine(raw?.summary || '', 180),
    metafields: normalizedMetafields,
    reviewFlags: normalizeReviewFlags(raw?.reviewFlags),
    missingFields: Array.isArray(raw?.missingFields) ? raw.missingFields : []
  }
}

export default useAIProcessing
